import React, { Component } from 'react'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/reset.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/site.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/container.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/segment.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/header.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/table.css'
import './styles.scss'
import { Masthead } from '@ta-interaktiv/react-masthead'
import { ImageCover } from 'components/cover'
import sheetrock from 'sheetrock'
import { zip } from 'd3-array'
import { HotelCategory } from 'components/hotelCategory'
import { CategoryMenu } from 'components/categoryMenu'
import { Sidebar } from 'components/slidingpane'
import { Waypoint } from 'react-waypoint'
import { ThemeSwitch } from '@ta-interaktiv/react-theme-switch'
import { categories } from '../../global/categories'
import { textToClassName } from '../../global/textToClassName'
import { Footer } from '@ta-interaktiv/react-footer'
import Intro from './intro.mdx'
import RatingExplanation from './ratingExplanation.mdx'
import { browserCheck } from '@ta-interaktiv/browsercheck'

const isMobile = browserCheck.isMobile
const posterImage = require(isMobile
  ? 'images/zermatterhof-mobile.jpg?sizes[]=640w&sizes[]=1280w&sizes[]=1800w&sizes[]=2360w&placeholder'
  : 'images/zermatterhof.jpg?sizes[]=640w&sizes[]=1280w&sizes[]=1800w&sizes[]=2360w&placeholder')

/**
 * The article ID of the corresponding CD story
 *
 * @todo Add actual article ID
 * @type {number}
 */
const ARTICLE_ID = '925694355438'

interface SheetrockResponse {
  attributes: {
    last: number
    rowNumberOffset: 0
    labels: string[]
  }
  html: string
  options: any
  raw: {
    version: string
    reqId: string
    status: string
    sig: string
    table: {
      cols: {
        id: string
        label: string
        type: string
        pattern?: string
      }[]
      rows: ({ v: string | number | null; f?: string } | null)[]
    }
  }
  rows: { cellsArray: string[]; labels: string[]; num: number }[]
}

interface State {
  request: 'loading' | 'error' | 'ready'
  hotelsByCategory: Map<string, Map<string, any>[]>
}

const ratingExplanationStyles = {
  h3: props => (
    <h3 className='ui tiny infographic dividing header' {...props} />
  ),
  ul: props => <div className='ui infographic icon list' {...props} />,
  li: props => (
    <div className='item'>
      <i className='right caret icon' />
      <div className='content' {...props} />
    </div>
  )
}

export class AppComponent extends Component<{}, State> {
  public state = {
    request: 'loading',
    hotelsByCategory: new Map()
  }

  public componentDidMount() {
    if (navigator.userAgent !== 'ReactSnap') {
      sheetrock({
        url:
          'https://docs.google.com/spreadsheets/d/1lCgfqybS82dIlWopAgGGOdkFHlOZHiTeBdT1_7rDvJk/edit?gid=0',
        query: 'select A,B,C,D,E,F,G,H,I,J,K,L,M,N,O,P where A != ""',
        callback: this.prepareData.bind(this)
      })
    }
  }

  /**
   * Prepare the hotel data and set the state once its ready
   * @param error
   * @param options
   * @param response
   */
  private prepareData(error, options, response: SheetrockResponse) {
    // Error handling
    if (error) {
      console.group('Main Component 🗂 Sheetrock Loader')
      console.log(error)
      console.groupEnd()
      this.setState({
        request: 'error'
      })
      return
    }

    const mapRows = response.rows.map(row => {
      return new Map(zip(row.labels, row.cellsArray))
    })

    // Check that we actually have data
    console.assert(mapRows[1].get('Kategorie') === 'Ferien')

    // Create a new map
    const hotelsByCategory: Map<string, Map<string, any>[]> = new Map()

    // Get the categories in the configuration and pull all the hotels for
    // each category out of the list
    Object.keys(categories).forEach(category => {
      hotelsByCategory.set(
        category,
        mapRows.filter(hotel => hotel.get('Kategorie') === category)
      )
    })

    // Push to state
    this.setState({
      hotelsByCategory,
      request: 'ready'
    })
  }

  public render() {
    return (
      <div className='index'>
        <Masthead
          articleId={ARTICLE_ID}
          hashtags={['switzerland', 'hotels', 'holidays']}
          mediaName='sonntagszeitung'
          inverted
        />
        <ThemeSwitch />
        {/*
        Cover Image
        /src/components/cover/image.js
        */}
        <ImageCover
          poster={posterImage}
          title='Die 125 besten Hotels der Schweiz'
          subtitle='Das grosse Rating vom Hotelexperten Karl Wild. Die Aufsteiger, die Absteiger, die Neuen.
          Und das Schweizer Hotel des Jahres 2021.'
          coverHeight={isMobile ? '85vh' : '95vh'}
          position='top'
          padding='8vh'
          showTextShadow={false}
          showTopGradient
        />

        <div className='ui container'>
          <div className='caption'>
            Hotel des Jahres: Das Hotel Zermatterhof gehört zu den edelsten
            Adressen im weltbekannten Walliser Ferienort. Foto: Vitor Cortes
          </div>
        </div>

        <main className='ui vertical segment'>
          <div className='ui text container ta'>
            <Intro />
          </div>
        </main>

        {/* Sidebar – sollte so weit noch korrekt sein */}
        <Sidebar title='So wurde bewertet' className='infographic'>
          <RatingExplanation components={ratingExplanationStyles} />
        </Sidebar>
        <div>
          {/*
        Menu «Die besten Hotels in den Kategorien …»
        /src/components/categoryMenu/component.js
        */}
          <div className='ui container'>
            <div className='ui bottom pointing large blue basic label'>
              Wählen Sie eine Kategorie
            </div>
          </div>
          <CategoryMenu />

          {/*
        Die Liste der Hotels. Diese Funktion nimmt die in
         /src/config/base.js hinterlegten Kategorienamen und generiert ein
          eigenes .ui.segment für jede der Kategorien.

          Bewertungs-Kategorie
           /src/components/hotelCategory/component.js
        */}
          {Object.keys(categories).map((category: string) => (
            <HotelCategory
              key={textToClassName(category)}
              category={category}
              id={`cat-${textToClassName(category)}`}
              hotelList={this.state.hotelsByCategory.get(category) || []}
              request={this.state.request}
            />
          ))}
        </div>

        <Waypoint
          scrollableAncestor={window}
          onEnter={event => {
            if (event.previousPosition === 'below') {
              window.dataLayer = window.dataLayer || []
              window.dataLayer.push({
                event: 'contentBottom'
              })
            }
          }}
        />

        <Footer
          hidePortfolioTeaser
          hideUserFeedback
          articleId={ARTICLE_ID}
          mediaName='sonntagszeitung'
          fullMediaName='SonntagsZeitung'
          hashtags={['holidays', 'switzerland', 'hotels']}
          credits={[
            ['Hotelranking und Texte', 'Karl Wild'],
            ['Digitale Umsetzung', 'Mathias Lutz, Andreas Moor, Kaspar Manz']
          ]}
        />

        <Waypoint
          scrollableAncestor={window}
          onEnter={event => {
            if (event.previousPosition === 'below') {
              window.dataLayer = window.dataLayer || []
              window.dataLayer.push({
                event: 'pageBottom'
              })
            }
          }}
        />
      </div>
    )
  }
}
